import React, { useEffect, useState, useMemo, useCallback } from "react";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import DataTable from "react-data-table-component";
import { Typography, MenuItem } from "@material-ui/core";
import axios from "axios";
import swal from "sweetalert";
import { useGlobalSpinnerActionsContext } from "../../context/GlobalSpinnerContext";
import moment from "moment";
import { Configuration } from "../../assets/data/config";
import { useActivityLogger } from "../../hooks/useActivityLogger";
import { apiServices } from "../../services/apiServices";
import DeleteIcon from "@material-ui/icons/Delete";

function downloadCSV(filteredData, columns, selectedItemType, selectedTxType) {
  if (!Array.isArray(filteredData) || filteredData.length === 0) return;

  // Step 1: Map data keys (selector) to readable names
  const headers = columns.map((col) => {
    return () => col.name;
  });
  const selectors = columns.map((col) => col.exportSelector);

  // Step 2: Format data for CSV
  const csvRows = [
    headers.map((getHeader, i) => getHeader(filteredData[0] || {})), // dynamic headers based on row values
    ...filteredData.map((row) =>
      selectors.map((selector) => {
        const value = row[selector];
        return value != null ? value : "";
      })
    ),
  ];

  // Step 3: Convert to CSV string
  const csvContent = csvRows.map((e) => e.join(",")).join("\n");

  // Step 4: Create download link
  const link = document.createElement("a");
  const now = new Date();
  const formattedDate = now
    .toISOString()
    .replace(/T/, "_")
    .replace(/:/g, "-")
    .replace(/\..+/, "");
  const filename = `${selectedItemType || "data"}_${formattedDate}.csv`;
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.click();

  URL.revokeObjectURL(url);
}

const Export = ({ onExport }) => (
  <Button
    color="primary"
    variant="contained"
    onClick={(e) => onExport(e.target.value)}
  >
    Export
  </Button>
);

const baseStructure = {
  id: "",
  txnDate: "",
  itemType: "",
  itemName: "",
  txnType: "RECEIVED FROM",
  txnTypeValue: "",
  clientName: "",
  clientUID: "",
  balanceOrStock: "0",
  qtyReceivedOrIssues: "",
  batchNo: "",
  expDate: "",
  txnTypeValueOther: "",
  totalBalanceOrStock: "",
  medicineType: "",
};
const requiredFields = [
  "txnDate",
  "itemType",
  "itemName",
  "txnType",
  "txnTypeValue",
  "clientName",
  "clientUID",
  // "balanceOrStock",
  "qtyReceivedOrIssues",
  "batchNo",
  "expDate",
  "txnTypeValueOther",
  // "totalBalanceOrStock",
];

const VendorForm = () => {
  const { sessionUserBoValue } = useActivityLogger();
  const userName = sessionUserBoValue?.displayName || "";
  const userId = sessionUserBoValue?.userCredentials?.id || null;

  const d = new Date();
  const TODAY = d.toISOString().split("T")[0];
  // const TODAYTIME = moment().format("YYYY-MM-DDTHH:mm");
  const TODAYTIME = moment().format("YYYY-MM-DD");

  // console.log("TODAYTIME", TODAYTIME);
  // console.log("process.env", process.env);
  // console.log("Configuration", Configuration);
  // console.log("sessionUserBoValue", sessionUserBoValue);

  const baseURL =
    process.env.NODE_ENV === "development"
      ? "http://localhost:9000/mrunner_api"
      : `${window.location.protocol}//${window.location.host}/mrunner_api`;
  // const baseURL = `${window.location.protocol}//${window.location.host}/mrunner_api`;
  // const baseURL = `https://tpttest.imonitorplus.com/mrunner_api`;

  const [formData, setFormData] = useState({
    ...baseStructure,
    txnDate: TODAYTIME,
  });
  const [masterList, setMasterList] = useState([]);
  const [transactionTypeList, setTransactionTypeList] = useState([]);
  const [itemTypeList, setItemTypeList] = useState([]);
  const [categoryTypeList, setCategoryTypeList] = useState([]);
  const [errors, setErrors] = useState({});
  const [data, setData] = useState([]);
  const [batchDetails, setBatchDetails] = useState([]);
  const [searchName, setSearchName] = useState("");

  const [selectedTxType, setSelectedTxType] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [selectedStartPeriod, setSelectedStartPeriod] = useState(null);
  const [selectedEndPeriod, setSelectedEndPeriod] = useState(null);

  const setGlobalSpinner = useGlobalSpinnerActionsContext();

  const showForm = useMemo(() => {
    if (!sessionUserBoValue) return false;

    const isAdmin = sessionUserBoValue.userCredentials?.userRoles?.some((r) =>
      Configuration.adminRoles.includes(r.id)
    );

    return !isAdmin; // return true only if user is NOT an admin
  }, [sessionUserBoValue]);

  const isProgramUser = useMemo(() => {
    if (!sessionUserBoValue) return false;

    return sessionUserBoValue.userCredentials?.userRoles?.some((r) =>
      Configuration.noDeleteOptions.includes(r.id)
    );
  }, [sessionUserBoValue]);

  const fetchData = useCallback(async () => {
    // setGlobalSpinner(true);
    const [mastersResult, txnTypeResult, ItemTypeResult, CategoryTypeResult] =
      await Promise.allSettled([
        axios.get(`${baseURL}/getMasters`),
        axios.get(`${baseURL}/getTransactionType`),
        axios.get(`${baseURL}/getItemType`),
        axios.get(`${baseURL}/getCategoryType`),
      ]);

    if (mastersResult.status === "fulfilled") {
      setMasterList(mastersResult?.value?.data || []);
    } else {
      console.error(
        "Error in fetching the master list...",
        mastersResult.reason
      );
    }

    if (txnTypeResult.status === "fulfilled") {
      setTransactionTypeList(
        txnTypeResult?.value?.data?.filter((d) => d.active_flag === "Y") || []
      );
    } else {
      console.error(
        "Error in fetching the transaction types...",
        txnTypeResult.reason
      );
    }

    if (ItemTypeResult.status === "fulfilled") {
      setItemTypeList(
        ItemTypeResult?.value?.data?.filter((d) => d.active_flag) || []
      );
    } else {
      console.error(
        "Error in fetching the medicine types...",
        ItemTypeResult.reason
      );
    }
    if (CategoryTypeResult.status === "fulfilled") {
      setCategoryTypeList(
        CategoryTypeResult?.value?.data?.filter((d) => d.active_flag) || []
      );
    } else {
      console.error(
        "Error in fetching the medicine types...",
        CategoryTypeResult.reason
      );
    }

    // setGlobalSpinner(false);
  }, [baseURL]);

  const fetchSummaryData = useCallback(async () => {
    try {
      const oData = await apiServices.getAPI(
        "organisationUnits.json?fields=id,displayName,parent[id,displayName]&paging=false"
      );
      // console.log("oData", oData);
      const response = await axios.get(`${baseURL}/getSummary`);
      // console.log("response", response);
      let sortedArray =
        response?.data?.sort(
          (a, b) => new Date(b.out_txndate) - new Date(a.out_txndate)
        ) || [];
      sortedArray = sortedArray
        .filter((s) => s.out_status)
        .map((s) => {
          const o =
            oData?.data?.organisationUnits?.find(
              (o) => o.id === s.out_facilityid
            ) || null;
          let fName = s?.out_facilityid || "NA";
          if (o) {
            fName = `${o.displayName} (${o.parent.displayName})`;
          }
          return {
            ...s,
            facilityID: s.out_facilityid,
            out_facilityid: fName,
          };
        });
      // console.log("sortedArray", sortedArray);
      setData(sortedArray);
    } catch (error) {
      console.log("Error in fetching the summary details...", error);
    } finally {
    }
  }, [baseURL]);

  const fetchAll = useCallback(async () => {
    setGlobalSpinner(true);

    await Promise.allSettled([fetchData(), fetchSummaryData()]);

    setGlobalSpinner(false);
  }, [fetchData, fetchSummaryData, setGlobalSpinner]);

  useEffect(() => {
    fetchAll();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Prevent negative quantities
    if (name === "qtyReceivedOrIssues" && Number(value) < 0) return;

    const updatedForm = {
      ...formData,
      [name]: value,
      facilityID: sessionUserBoValue?.organisationUnits?.[0]?.id || null,
    };
    if (name !== "qtyReceivedOrIssues") {
      updatedForm.qtyReceivedOrIssues = 0;
      updatedForm.totalBalanceOrStock = 0;
    }

    // Auto-calculate Total Stock and Closing Balance
    const open = parseInt(updatedForm.balanceOrStock || "0", 10);
    const received = parseInt(updatedForm.qtyReceivedOrIssues || "0", 10);

    if (updatedForm.txnType === "RECEIVED FROM") {
      updatedForm.totalBalanceOrStock = (open + received).toString();
    } else {
      const balance = open - received;
      if (balance < 0) {
        swal({
          title: "Error",
          text: "You cannot allocate more than available stock...",
          icon: "error",
          button: "Close",
        });
        updatedForm.qtyReceivedOrIssues = open;
        updatedForm.totalBalanceOrStock = 0;
      } else {
        updatedForm.totalBalanceOrStock = (open - received).toString();
      }
    }

    // console.log("updatedForm", updatedForm);
    setFormData(updatedForm);
  };

  const validateForm = () => {
    let formErrors = {};
    let isValid = true;

    const rFields =
      formData.txnTypeValue === 15
        ? requiredFields.filter((r) => !["txnTypeValueOther"].includes(r))
        : formData.txnTypeValue === 14
        ? requiredFields.filter((r) => !["clientName", "clientUID"].includes(r))
        : requiredFields.filter(
            (r) => !["clientName", "clientUID", "txnTypeValueOther"].includes(r)
          );
    rFields.forEach((field) => {
      if (!formData[field]) {
        formErrors[field] = "This field is required";
        isValid = false;
      }
    });
    setErrors(formErrors);
    return isValid;
  };

  useEffect(() => {
    if (formData.itemName && formData.batchNo && formData.facilityID) {
      const fetchBalance = async () => {
        setGlobalSpinner(true);

        try {
          const response = await axios.get(`${baseURL}/getBalance`, {
            params: {
              itemName: formData.itemName,
              batchNo: formData.batchNo,
              facilityID: formData.facilityID,
            },
          });
          // console.log("response", response);
          setFormData((prev) => ({
            ...prev,
            balanceOrStock: response?.data?.[0]?.out_quantity || 0,
            qtyReceivedOrIssues: 0,
            totalBalanceOrStock: 0,
          }));
        } catch (error) {
          console.log("Error in fetching balance...", error);
        } finally {
          setGlobalSpinner(false);
        }
      };
      fetchBalance();
    }
  }, [
    formData.itemName,
    formData.batchNo,
    formData.facilityID,
    baseURL,
    setGlobalSpinner,
  ]);

  useEffect(() => {
    if (formData.txnType === "ISSUED TO" && formData.itemName) {
      const fetchBatchDetails = async () => {
        setGlobalSpinner(true);

        try {
          const response = await axios.get(
            `${baseURL}/getBatchDetails?itemName=${formData.itemName}`
          );
          // console.log("response", response);
          setBatchDetails(response?.data || []);
        } catch (error) {
          console.log("Error in fetching balance...", error);
        } finally {
          setGlobalSpinner(false);
        }
      };
      fetchBatchDetails();
    }
  }, [formData.itemName, formData.txnType, baseURL, setGlobalSpinner]);

  const handleSubmit = async (e) => {
    // console.log("e", e);
    e.preventDefault();
    if (!validateForm()) return;
    setGlobalSpinner(true);
    const data = {
      transaction_key: null,
      user_key: "1",
      txnTypeValue: formData.txnTypeValue,
      txnType: formData.txnType,
      itemName: formData.itemName,
      txnDate: formData.txnDate,
      batchNo: formData.batchNo,
      expDate: formData.expDate,
      qtyReceivedOrIssues: formData.qtyReceivedOrIssues,
      clientUID: formData.clientUID,
      txnTypeValueOther: formData?.txnTypeValueOther || null,
      facilityID:
        formData?.facilityID ||
        sessionUserBoValue?.organisationUnits?.[0]?.id ||
        null,
      actionType: "create",
      user: userName,
    };

    try {
      await axios.post(`${baseURL}/updateTransaction`, data, {
        headers: { "Content-Type": "application/json" },
      });
      swal({
        title: "Success",
        text: "Data saved successfully...",
        icon: "success",
        button: "Close",
      });
    } catch (error) {
      console.log("Error while saving the data...", error);
      swal({
        title: "Error",
        text: "Error while saving the data...",
        icon: "error",
        button: "Close",
      });
    } finally {
      resetValues();
      setGlobalSpinner(false);
      fetchAll();
    }
  };

  const resetValues = useCallback(() => {
    setFormData({
      ...baseStructure,
      txnDate: TODAYTIME,
    });
    setErrors({});
  }, [TODAYTIME]);

  const updateData = useCallback(
    async (data) => {
      setGlobalSpinner(true);
      try {
        await axios.post(`${baseURL}/updateTransaction`, data, {
          headers: { "Content-Type": "application/json" },
        });
        swal({
          title: "Success",
          text:
            data.txnType === "delete"
              ? "Data deleted successfully..."
              : "Data saved successfully...",
          icon: "success",
          button: "Close",
        }).then((value) => {
          if (value) {
            fetchAll();
            axios.post(
              process.env.NODE_ENV === "development"
                ? `http://localhost:9000/mrunner_api/refreshDashboards`
                : `${window.location.protocol}//${window.location.host}/mrunner_api/refreshDashboards`,
              { r_id: 4 }
            );
          }
        });
      } catch (error) {
        console.log("Error while saving the data...", error);
        swal({
          title: "Error",
          text: "Error while saving the data...",
          icon: "error",
          button: "Close",
        });
      } finally {
        resetValues();
        setGlobalSpinner(false);
      }
    },
    [baseURL, fetchAll, resetValues, setGlobalSpinner]
  );

  const onDelete = useCallback(
    (row) => {
      swal({
        title: "Are you sure?",
        text: "Once deleted, you will not be able to recover this record!",
        icon: "warning",
        buttons: ["Cancel", "Delete"],
        dangerMode: true,
      }).then((willDelete) => {
        if (willDelete) {
          const data = {
            transaction_key: row.out_transaction_key,
            user: userName,
            actionType: "delete",
          };
          updateData(data);
        } else {
          console.log("Your record is safe!");
        }
      });
    },
    [userName, updateData]
  );

  const columns = useMemo(() => {
    const cols = [
      {
        name: "Transaction Date",
        selector: (row) => row.out_txndate,
        exportSelector: "out_txndate",
        sortable: true,
        wrap: true,
      },
      {
        name: "Transaction Type",
        selector: (row) => row.out_txntype,
        exportSelector: "out_txntype",
        sortable: true,
        wrap: true,
      },
      {
        name: "Facility Name",
        selector: (row) => row.out_facilityid,
        exportSelector: "out_facilityid",
        sortable: true,
        wrap: true,
      },
      {
        name: "Item Type",
        selector: (row) => row.out_itemtype,
        exportSelector: "out_itemtype",
        sortable: true,
        wrap: true,
      },
      {
        name: "Item Name",
        selector: (row) => row.out_itemname,
        exportSelector: "out_itemname",
        sortable: true,
        wrap: true,
      },
      {
        name: "Batch No.",
        selector: (row) => row.out_batchno,
        exportSelector: "out_batchno",
        sortable: true,
        wrap: true,
      },
      {
        name: "Expiry Date",
        selector: (row) => row.out_expirydate,
        exportSelector: "out_expirydate",
        sortable: true,
        wrap: true,
      },
      {
        name:
          selectedTxType === "RECEIVED FROM" ? "Opening Stock" : "Total Stock",
        selector: (row) => row.out_balanceorstock,
        exportSelector: "out_balanceorstock",
        sortable: true,
        wrap: true,
      },
      {
        name:
          selectedTxType === "RECEIVED FROM" ? "Received from" : "Issued to",
        selector: (row) => row.out_txntypevalue,
        exportSelector: "out_txntypevalue",
        sortable: true,
        wrap: true,
      },
      {
        name:
          selectedTxType === "RECEIVED FROM"
            ? "Quantity Received"
            : "Quantity Issued",
        selector: (row) => row.out_qtyreceivedorissues,
        exportSelector: "out_qtyreceivedorissues",
        sortable: true,
        wrap: true,
      },
      {
        name:
          selectedTxType === "RECEIVED FROM" ? "Total Stock" : "Closing Stock",
        selector: (row) => row.out_totalbalanceorstock,
        exportSelector: "out_totalbalanceorstock",
        sortable: true,
        wrap: true,
      },
    ];

    if (!showForm && !isProgramUser) {
      cols.push({
        name: "Action",
        selector: null,
        sortable: true,
        exclude: true,
        wrap: true,
        width: "150px",
        cell: (row) => (
          <>
            <Button
              variant="contained"
              color="secondary"
              component="span"
              startIcon={<DeleteIcon />}
              onClick={() => onDelete(row)}
            >
              Delete
            </Button>
          </>
        ),
      });
    }
    return cols;
  }, [selectedTxType, showForm, onDelete, isProgramUser]);

  // const itemTypeList = useMemo(() => {
  //   return masterList.length > 0
  //     ? Array.from(
  //         new Map(masterList.map((item) => [item["Item Type"], item])).values()
  //       )
  //     : [];
  // }, [masterList]);

  const itemNameList = useMemo(() => {
    return masterList.length > 0
      ? masterList.filter(
          (item) =>
            item.item_type_key === formData.itemType &&
            item.category_type_key === formData.medicineType
        )
      : [];
  }, [masterList, formData.itemType, formData.medicineType]);

  const txnTypeList = useMemo(() => {
    return transactionTypeList.length > 0
      ? Array.from(
          new Map(
            transactionTypeList.map((item) => [item.action_type, item])
          ).values()
        )
      : [];
  }, [transactionTypeList]);

  useEffect(() => {
    if (txnTypeList?.length > 0) {
      setSelectedTxType(txnTypeList[0].action_type);
    }
  }, [txnTypeList]);

  const txnTypeValueList = useMemo(() => {
    if (transactionTypeList.length === 0) return [];

    let filteredList = transactionTypeList.filter(
      (item) => item.action_type === formData.txnType
    );

    if (formData.txnType === "RECEIVED FROM") {
      // Move item with action_key === 14 to the end
      const withoutKey14 = filteredList.filter(
        (item) => item.action_key !== 14
      );
      const withKey14 = filteredList.find((item) => item.action_key === 14);

      if (withKey14) {
        filteredList = [...withoutKey14, withKey14];
      } else {
        filteredList = withoutKey14;
      }
    }

    return filteredList;
  }, [transactionTypeList, formData.txnType]);

  const batchDetailsList = useMemo(() => {
    const seen = new Set();
    const uniqueList = [];

    for (const item of batchDetails) {
      if (!seen.has(item.out_batchno)) {
        seen.add(item.out_batchno);
        uniqueList.push(item); // Keep full object
      }
    }

    return uniqueList;
  }, [batchDetails]);

  const expiryDateByBatch = useMemo(() => {
    let a = null;
    if (
      formData.txnType === "ISSUED TO" &&
      formData.itemName &&
      formData.batchNo
    ) {
      a = batchDetails.find(
        (b) => b.out_batchno === formData.batchNo
      )?.out_expiry_date;
    }
    return a;
  }, [batchDetails, formData.txnType, formData.batchNo, formData.itemName]);

  useEffect(() => {
    if (expiryDateByBatch) {
      setFormData((prev) => ({
        ...prev,
        expDate: expiryDateByBatch.split("T")[0],
      }));
    }
  }, [expiryDateByBatch]);

  const byFacilityData = useMemo(() => {
    let fData = [];
    if (sessionUserBoValue) {
      fData = [...data];
      if (showForm) {
        fData = fData.filter(
          (d) => d.facilityID === sessionUserBoValue?.organisationUnits?.[0]?.id
        );
      }
    }
    return fData;
  }, [sessionUserBoValue, data, showForm]);

  const filteredData = useMemo(() => {
    let uData = [...byFacilityData];

    if (selectedTxType) {
      uData = uData.filter((d) => d.out_txntype === selectedTxType);
    }

    if (selectedItemType) {
      uData = uData.filter((d) => d.out_itemtype === selectedItemType);
    }

    if (selectedStartPeriod && selectedEndPeriod) {
      uData = uData.filter((d) =>
        moment(d.out_txndate).isBetween(
          selectedStartPeriod,
          selectedEndPeriod,
          "day",
          "[]"
        )
      );
    }

    return uData.filter(
      (d) =>
        d.out_txndate.toLowerCase().includes(searchName.toLowerCase()) ||
        d.out_txntype.toLowerCase().includes(searchName.toLowerCase()) ||
        d.out_itemtype.toLowerCase().includes(searchName.toLowerCase()) ||
        d.out_itemname.toLowerCase().includes(searchName.toLowerCase()) ||
        d.out_txntypevalue.toLowerCase().includes(searchName.toLowerCase())
    );
  }, [
    searchName,
    byFacilityData,
    selectedTxType,
    selectedItemType,
    selectedStartPeriod,
    selectedEndPeriod,
  ]);

  const actionsMemo = React.useMemo(
    () => (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "end",
        }}
      >
        <div style={{ width: "200px", marginBottom: "10px" }}>
          <label style={{ paddingLeft: "20px" }}>Transaction Type</label>
          <TextField
            select
            name="txnType"
            value={selectedTxType || ""}
            onChange={(e) => setSelectedTxType(e.target.value)}
            fullWidth
            variant="outlined"
            className="p-0"
            placeholder="Transaction Type"
          >
            <MenuItem value="" disabled>
              Select Transaction Type
            </MenuItem>
            {txnTypeList.length > 0 ? (
              txnTypeList.map((item, i) => (
                <MenuItem value={item.action_type} key={"txnType" + i}>
                  {item.action_type}
                </MenuItem>
              ))
            ) : (
              <MenuItem value={null} disabled>
                No Data to Display
              </MenuItem>
            )}
          </TextField>
        </div>
        <div style={{ width: "300px", marginBottom: "10px" }}>
          <label style={{ paddingLeft: "20px" }}>Item Type</label>
          <TextField
            select
            name="itemType"
            value={selectedItemType || "All"}
            onChange={(e) => setSelectedItemType(e.target.value)}
            fullWidth
            variant="outlined"
            className="p-0"
            placeholder="Item Type"
          >
            <MenuItem value="All" disabled>
              Select Item Type
            </MenuItem>
            <MenuItem value={null}>None</MenuItem>
            {itemTypeList.length > 0 ? (
              itemTypeList.map((item, i) => (
                <MenuItem value={item.item_type_name} key={"itemType" + i}>
                  {item.item_type_name}
                </MenuItem>
              ))
            ) : (
              <MenuItem value={null} disabled>
                No Data to Display
              </MenuItem>
            )}
          </TextField>
        </div>
        <div style={{ width: "200px", marginBottom: "10px" }}>
          <label style={{ paddingLeft: "20px" }}>Start Date</label>
          <TextField
            type="date"
            name="startDate"
            value={selectedStartPeriod}
            onChange={(e) => setSelectedStartPeriod(e.target.value)}
            fullWidth
            variant="outlined"
            className="p-0"
            InputLabelProps={{ shrink: true }}
          />
        </div>
        <div style={{ width: "200px", marginBottom: "10px" }}>
          <label style={{ paddingLeft: "20px" }}>End Date</label>
          <TextField
            type="date"
            name="endDate"
            value={selectedEndPeriod}
            onChange={(e) => setSelectedEndPeriod(e.target.value)}
            fullWidth
            variant="outlined"
            className="p-0"
            InputLabelProps={{ shrink: true }}
          />
        </div>
        <div
          style={{
            width: "200px",
            marginBottom: "10px",
            paddingLeft: "20px",
          }}
        >
          <label style={{ paddingLeft: "20px" }}>Search</label>
          <input
            style={{
              width: "90%",
              padding: "10px",
              borderRadius: "4px",
              border: "1px solid rgba(0, 0, 0, 0.26)",
            }}
            placeholder="Search..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
          />
        </div>
        <div style={{ marginBottom: "10px", paddingLeft: "20px" }}>
          <Export
            onExport={() =>
              downloadCSV(
                filteredData,
                columns,
                selectedItemType,
                selectedTxType
              )
            }
          />
        </div>
      </div>
    ),
    [
      filteredData,
      columns,
      searchName,
      itemTypeList,
      selectedItemType,
      selectedTxType,
      txnTypeList,
      selectedEndPeriod,
      selectedStartPeriod,
    ]
  );

  const filteredCategoryTypeList = React.useMemo(() => {
    return categoryTypeList.filter(
      (c) => Number(c.item_type_key) === Number(formData.itemType)
    );
  }, [categoryTypeList, formData.itemType]);

  return (
    <>
      {showForm && (
        <div style={{ marginBottom: "30px" }}>
          <form onSubmit={handleSubmit} className="inventoryForm">
            <Grid container spacing={3} className="inventorySection">
              <Grid item xs={12} sm={3}>
                <Typography>
                  Transaction Date<sup style={{ color: "red" }}>*</sup>
                </Typography>
                <TextField
                  type="date"
                  name="txnDate"
                  value={formData.txnDate}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.txnDate}
                  helperText={errors.txnDate}
                  inputProps={{
                    max: TODAYTIME,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography>
                  Transaction Type<sup style={{ color: "red" }}>*</sup>
                </Typography>
                <TextField
                  select
                  name="txnType"
                  value={formData.txnType}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  error={!!errors.txnType}
                  helperText={errors.txnType}
                >
                  {txnTypeList.length > 0 ? (
                    txnTypeList.map((item, i) => (
                      <MenuItem value={item.action_type} key={"txnType" + i}>
                        {item.action_type}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={null} disabled>
                      No Data to Display
                    </MenuItem>
                  )}
                </TextField>
              </Grid>
              {formData.txnType && (
                <Grid item xs={12} sm={3}>
                  <Typography>
                    {formData.txnType === "RECEIVED FROM"
                      ? "Received From"
                      : "Issued To"}
                    <sup style={{ color: "red" }}>*</sup>
                  </Typography>
                  <TextField
                    select
                    name="txnTypeValue"
                    value={formData.txnTypeValue}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    error={!!errors.txnTypeValue}
                    helperText={errors.txnTypeValue}
                  >
                    {txnTypeValueList.length > 0 ? (
                      txnTypeValueList.map((item, i) => (
                        <MenuItem
                          value={item.action_key}
                          key={"txnTypeValue" + i}
                        >
                          {item.action_name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value={null} disabled>
                        No Data to Display
                      </MenuItem>
                    )}
                  </TextField>
                </Grid>
              )}
              {formData.txnTypeValue === 14 && (
                <Grid item xs={12} sm={3}>
                  <Typography>
                    Received From (Other)<sup style={{ color: "red" }}>*</sup>
                  </Typography>
                  <TextField
                    name="txnTypeValueOther"
                    value={formData.txnTypeValueOther}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    error={!!errors.txnTypeValueOther}
                    helperText={errors.txnTypeValueOther}
                  />
                </Grid>
              )}
              {formData.txnTypeValue === 15 && (
                <>
                  <Grid item xs={12} sm={3}>
                    <Typography>
                      Client Name<sup style={{ color: "red" }}>*</sup>
                    </Typography>
                    <TextField
                      name="clientName"
                      value={formData.clientName}
                      onChange={handleInputChange}
                      fullWidth
                      variant="outlined"
                      error={!!errors.clientName}
                      helperText={errors.clientName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography>
                      Client UID<sup style={{ color: "red" }}>*</sup>
                    </Typography>
                    <TextField
                      name="clientUID"
                      value={formData.clientUID}
                      onChange={handleInputChange}
                      fullWidth
                      variant="outlined"
                      error={!!errors.clientUID}
                      helperText={errors.clientUID}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={3}>
                <Typography>
                  Item Type<sup style={{ color: "red" }}>*</sup>
                </Typography>
                <TextField
                  select
                  name="itemType"
                  value={formData.itemType}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  error={!!errors.itemType}
                  helperText={errors.itemType}
                >
                  {itemTypeList.length > 0 ? (
                    itemTypeList.map((item, i) => (
                      <MenuItem value={item.item_type_key} key={"itemType" + i}>
                        {item.item_type_name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={null} disabled>
                      No Data to Display
                    </MenuItem>
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography>
                  Item Category<sup style={{ color: "red" }}>*</sup>
                </Typography>
                <TextField
                  select
                  name="medicineType"
                  value={formData.medicineType}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  error={!!errors.medicineType}
                  helperText={errors.medicineType}
                >
                  {filteredCategoryTypeList.length > 0 ? (
                    filteredCategoryTypeList.map((item, i) => (
                      <MenuItem
                        value={item.category_type_key}
                        key={"medicineType" + i}
                      >
                        {item.category_type_name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={null} disabled>
                      No Data to Display
                    </MenuItem>
                  )}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Typography>
                  Item Name<sup style={{ color: "red" }}>*</sup>
                </Typography>
                <TextField
                  select
                  name="itemName"
                  value={formData.itemName}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  error={!!errors.itemName}
                  helperText={errors.itemName}
                >
                  {itemNameList.length > 0 ? (
                    itemNameList.map((item, i) => (
                      <MenuItem value={item.item_key} key={"itemName" + i}>
                        {item.item_name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={null} disabled>
                      No Data to Display
                    </MenuItem>
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography>
                  Batch No<sup style={{ color: "red" }}>*</sup>
                </Typography>
                {formData.txnType === "RECEIVED FROM" ? (
                  <TextField
                    name="batchNo"
                    value={formData.batchNo}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    error={!!errors.batchNo}
                    helperText={errors.batchNo}
                  />
                ) : (
                  <TextField
                    select
                    name="batchNo"
                    value={formData.batchNo}
                    onChange={handleInputChange}
                    fullWidth
                    variant="outlined"
                    error={!!errors.batchNo}
                    helperText={errors.batchNo}
                  >
                    {batchDetailsList.length > 0 ? (
                      batchDetailsList.map((item, i) => (
                        <MenuItem value={item.out_batchno} key={"batchNo" + i}>
                          {item.out_batchno}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value={null} disabled>
                        No Data to Display
                      </MenuItem>
                    )}
                  </TextField>
                )}
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography>
                  Expiry Date<sup style={{ color: "red" }}>*</sup>
                </Typography>
                <TextField
                  type="date"
                  name="expDate"
                  value={formData.expDate}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.expDate}
                  helperText={errors.expDate}
                  inputProps={{ min: TODAY }}
                  disabled={formData.txnType === "ISSUED TO"}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography>
                  {formData.txnType === "RECEIVED FROM"
                    ? "Opening Balance"
                    : "Total Stock"}
                  <sup style={{ color: "red" }}>*</sup>
                </Typography>
                <TextField
                  name="balanceOrStock"
                  value={formData.balanceOrStock}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  error={!!errors.balanceOrStock}
                  helperText={errors.balanceOrStock}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography>
                  {formData.txnType === "RECEIVED FROM"
                    ? "Qty Received"
                    : "Qty Issued"}
                  <sup style={{ color: "red" }}>*</sup>
                </Typography>
                <TextField
                  type="number"
                  name="qtyReceivedOrIssues"
                  value={formData.qtyReceivedOrIssues}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  error={!!errors.qtyReceivedOrIssues}
                  helperText={errors.qtyReceivedOrIssues}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography>
                  {formData.txnType === "RECEIVED FROM"
                    ? "Total Stock"
                    : "Closing Balance"}
                  <sup style={{ color: "red" }}>*</sup>
                </Typography>
                <TextField
                  name="totalBalanceOrStock"
                  value={formData.totalBalanceOrStock}
                  fullWidth
                  variant="outlined"
                  disabled
                  error={!!errors.totalBalanceOrStock}
                  helperText={errors.totalBalanceOrStock}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3} style={{ marginTop: "20px" }}>
              <Grid item xs={8}></Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  Submit
                </Button>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  onClick={resetValues}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </form>
        </div>
      )}
      <div className="inventoryTable" style={{ marginTop: "0px" }}>
        <DataTable
          title="Summary Report"
          columns={columns}
          data={filteredData}
          pagination
          highlightOnHover
          striped
          persistTableHead
          fixedHeader
          actions={actionsMemo}
        />
      </div>
    </>
  );
};

export default VendorForm;
